// File: CaseBlocksRenderer.tsx
/// HIGHLIGHTING sub-blocks makes markdown more prominent as **text** bold ??
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FinalizedReportsLinks from './components/FinalizedReportsLinks';

import { Box,
  Button,
  Card,
  CardContent,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  Tooltip,
  IconButton,
  Stack,
  Alert
 } from "@mui/material";
import AuthService from "../../services/AuthService";
import { getCaseBlocks, getCaseExtractions } from "../../services/caseApi";
import { getActiveVersion } from "../../services/reportAPI";
import ReactMarkdown from "react-markdown";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import ArticleIcon from '@mui/icons-material/Article';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import ImageIcon from '@mui/icons-material/Image';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// ----- Types -----
// ...existing code...
// ----- Types -----
type BlockType = "meta" | "image" | "segmentation" | "xai" | "llm" | "text" | "fileInfo" | "detection";

interface BlockData {
  [key: string]: any;
}

// ...existing code...
interface SubBlock {
  id: string;
  blockId: string; // Parent block ID for tracking after moving
  content: string | {
    type: 'image';
    url: string;
    label: string;
  };
  isVisible: boolean;
}
// ...existing code...
interface Block {
  id: string;
  type: BlockType;
  data: BlockData;
  positionIndex: number;
  subBlocks?: SubBlock[]; // Add support for sub-blocks
}

interface CaseBlocksRendererProps {
  caseId: string;
  generateReport?: boolean; // Flag to enable report mode
}

// ----- Block Components -----

const TextBlock: React.FC<{
  data: BlockData;
  onChange: (value: string) => void;
  onEnhance: (value: string) => void;
}> = ({ data, onChange, onEnhance }) => {
  const theme = useTheme();
  
  return (
    
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.text.primary, 0.08)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500
        }}
      >
        Editable Note
      </Typography>
      
      <textarea
        value={data.text || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        style={{ 
          width: "100%", 
          padding: "12px",
          borderRadius: "8px",
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          resize: "vertical",
          fontFamily: "inherit",
          fontSize: "inherit",
          marginBottom: "12px"
        }}
      />
      
      <Button 
        size="small" 
        onClick={() => onEnhance(data.text)} 
        startIcon={<FormatPaintIcon />}
        sx={{ 
          mt: 1,
          borderRadius: 1.5,
          background: `linear-gradient(45deg, ${theme.palette.info.main} 30%, ${alpha(theme.palette.info.main, 0.8)} 90%)`,
          color: 'white',
          px: 2,
          py: 0.5,
          '&:hover': {
            boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.4)}`,
          }
        }}
      >
        Enhance with AI
      </Button>
    </Paper>
  );
};

const ImageBlock: React.FC<{ data: BlockData }> = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.text.primary, 0.08)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500
        }}
      >
        <ImageIcon color="primary" fontSize="small" />
        Image
      </Typography>
      
      <Box 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.1)}`,
        }}
      >
        <img
          src={data.url}
          alt="Case content"
          style={{ 
            maxWidth: "100%", 
            maxHeight: "500px",
            objectFit: "contain" 
          }}
        />
      </Box>
    </Paper>
  );
};


// ...existing code...
const SegmentationBlock: React.FC<{ 
  blockId: string;
  data: BlockData;
  subBlocks?: SubBlock[];
  onToggleVisibility?: (id: string) => void;
  enableSubBlocks?: boolean;
}> = ({ 
  blockId, 
  data, 
  subBlocks,
  onToggleVisibility,
  enableSubBlocks 
}) => {
  const theme = useTheme();
  
  // Base URL for images
  const baseUrl = process.env.REACT_APP_API_URL_;
  
  if (enableSubBlocks && subBlocks) {
    // Render as draggable sub-blocks in report mode
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          transition: 'all 0.2s',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
          backgroundColor: alpha(theme.palette.secondary.main, 0.04),
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}`,
          }
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            fontWeight: 500,
            color: theme.palette.secondary.dark
          }}
        >
          <FormatPaintIcon color="secondary" fontSize="small" />
          Segmentation Analysis
        </Typography>
        
        <Box>
              {subBlocks.map((subBlock, index) => {
                if (typeof subBlock.content !== 'string' && subBlock.content.type === 'image') {
                  return (
                        <Box
                          key={subBlock.id}
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            my: 2,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                            opacity: subBlock.isVisible ? 1 : 0.5,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            transition: "all 0.2s ease",
                            '&:hover': {
                              boxShadow: `0 4px 8px ${alpha(theme.palette.text.primary, 0.1)}`,
                              borderColor: alpha(theme.palette.secondary.main, 0.3)
                            }
                          }}
                        >
                          <Stack 
                            direction="row" 
                            justifyContent="flex-end" 
                            alignItems="center" 
                            mb={1.5}
                            spacing={1}
                          >
                            <Tooltip title={subBlock.isVisible ? "Hide" : "Show"}>
                              <IconButton 
                                size="small" 
                                color={!subBlock.isVisible ? "error" : "default"}
                                onClick={() => onToggleVisibility?.(subBlock.id)}
                              >
                                {subBlock.isVisible ? 
                                  <VisibilityIcon fontSize="small" /> : 
                                  <VisibilityOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          <Box sx={{ textAlign: "center" }}>
                            <Chip
                              size="small"
                              label={typeof subBlock.content !== 'string' ? subBlock.content.label : ''}
                              color="default"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                            <Box 
                              sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden'
                              }}
                            >
                              <img 
                                src={typeof subBlock.content !== 'string' ? subBlock.content.url : ''} 
                                alt={typeof subBlock.content !== 'string' ? subBlock.content.label : ''}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '280px', 
                                  objectFit: 'contain',
                                  borderRadius: '4px'
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                  );
                }
                return null;
              })}
        </Box>
      </Paper>
    );
  }

  // Original non-report mode rendering with improved styling
  const imagePaths = [
    { label: "Segmented Image", path: data.segmentedPlot },
    { label: "Asymmetry Plot 1", path: data.assymetryPlot1 },
    { label: "Asymmetry Plot 2", path: data.assymetryplot2 },
    { label: "Color Histogram", path: data.colorplot },
  ];

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500,
          color: theme.palette.text.primary
        }}
      >
        <FormatPaintIcon color="secondary" fontSize="small" />
        Segmentation Analysis
      </Typography>

      <Stack spacing={2}>
        {imagePaths.map((img, idx) => {
          if (!img.path) return null;
          const fullUrl = baseUrl + img.path.replace(/^\/+/, "");
          return (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
                }
              }}
            >
              <Chip 
                label={img.label}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ mb: 1, color: theme.palette.text.primary }}
              />
              
              <Box 
                sx={{ 
                  borderRadius: 1, 
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={fullUrl}
                  alt={img.label}
                  style={{ 
                    maxWidth: "100%", 
                    maxHeight: "280px",
                    objectFit: "contain",
                    borderRadius: '4px'
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};
// ...existing code...

const XAIBlock: React.FC<{ data: BlockData }> = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.05),
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500,
          color: theme.palette.info.dark
        }}
      >
        <ArticleIcon color="info" fontSize="small" />
        Explainable AI
      </Typography>
      
      <Box 
        sx={{ 
          p: 2,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          whiteSpace: "pre-line"
        }}
      >
        <ReactMarkdown>{data.response}</ReactMarkdown>
      </Box>
    </Paper>
  );
};

const LLMNoteBlock: React.FC<{ 
  blockId: string;
  data: BlockData;
  subBlocks?: SubBlock[];
  onSubBlockChange?: (id: string, content: string) => void;
  onToggleHighlight?: (id: string) => void;
  onToggleVisibility?: (id: string) => void;
  onToggleEdit?: (id: string) => void;
  enableSubBlocks?: boolean;
}> = ({ 
  blockId,
  data, 
  subBlocks, 
  onSubBlockChange, 
  onToggleHighlight,
  onToggleVisibility,
  onToggleEdit,
  enableSubBlocks 
}) => {
  const theme = useTheme();
  
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500,
          color: theme.palette.primary.dark
        }}
      >
        <ArticleIcon color="primary" fontSize="small" />
        LLM Analysis
      </Typography>

      {enableSubBlocks && subBlocks ? (
        // Render as editable sub-blocks
        <Box sx={{ mt: 2 }}>
          {/* <Droppable droppableId={`block-${blockId}`} type="SUB_BLOCK">
            {(provided) => (
              <Box 
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {subBlocks.map((subBlock, index) => (
                  <SubBlockEditor
                    key={subBlock.id}
                    blockId={blockId}
                    subBlock={subBlock}
                    index={index}
                    onChange={onSubBlockChange || (() => {})}
                    onToggleHighlight={onToggleHighlight || (() => {})}
                    onToggleVisibility={onToggleVisibility || (() => {})}
                    onToggleEdit={onToggleEdit || (() => {})}
                  />
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable> */}
        </Box>
      ) : (
        // Original rendering with improved theme styling
        <Box 
          sx={{ 
            p: 2,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            whiteSpace: "pre-line"
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: "bold", 
              mb: 1.5,
              color: theme.palette.text.primary
            }}
          >
            Response:
          </Typography>
          <Box sx={{ color: theme.palette.text.primary }}>
            <ReactMarkdown>{data.response}</ReactMarkdown>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

const MetadataBlock: React.FC<{ data: BlockData }> = ({ data }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      variant="outlined"
      sx={{
        p: { xs: 1, sm: 1.5 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.08)}`,
        }
      }}
    >
      <Chip 
        label={`Case ID: ${data.caseId}`}
        icon={<ArticleIcon fontSize="small" />}
        size="small"
        variant="outlined"
        sx={{ 
          color: theme.palette.text.primary,
          borderColor: alpha(theme.palette.text.primary, 0.3),
          '&:hover': { opacity: 0.9 },
          transition: 'all 0.2s'
        }}
      />
      {data.patientId && (
        <Chip 
          label={`Patient ID: ${data.patientId}`}
          icon={<ArticleIcon fontSize="small" />}
          size="small"
          variant="outlined"
          sx={{ 
            color: theme.palette.text.primary,
            borderColor: alpha(theme.palette.text.primary, 0.3),
            '&:hover': { opacity: 0.9 },
            transition: 'all 0.2s'
          }}
        />
      )}
    </Paper>
  );
};// ...existing code...
const DetectionBlock: React.FC<{ 
  blockId: string;
  data: BlockData;
  subBlocks?: SubBlock[];
  onToggleVisibility?: (id: string) => void;
  enableSubBlocks?: boolean;
}> = ({ 
  blockId,
  data, 
  subBlocks,
  onToggleVisibility,
  enableSubBlocks
}) => {
  const theme = useTheme();
  const baseUrl = process.env.REACT_APP_API_URL_;
  const imageRef = React.useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  
  // Extract detection data
  const { x_min, y_min, x_max, y_max, confidence, imageFile } = data;
  const hasCoordinates = typeof x_min === 'number' && 
                         typeof y_min === 'number' && 
                         typeof x_max === 'number' && 
                         typeof y_max === 'number';

  // Format coordinates as percentages
  const formatCoord = (coord: number) => `${Math.round(coord * 100)}%`;
  
  // Create image URL
  const imageUrl = imageFile ? (
    imageFile.startsWith('http') ? imageFile :
    `${baseUrl}${imageFile.replace(/^\/+/, '')}`
  ) : '';

  // Calculate bounding box position based on actual image dimensions
  const getBoundingBoxStyle = () => {
    if (!imageRef.current || !imageLoaded || !hasCoordinates) return {};
    
    const img = imageRef.current;
    const container = img.parentElement;
    if (!container) return {};
    
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    
    // Get actual displayed image dimensions
    const displayedWidth = img.offsetWidth;
    const displayedHeight = img.offsetHeight;
    
    // Calculate offset (centering) - image is centered in container due to flex
    const offsetX = (containerWidth - displayedWidth) / 2;
    const offsetY = (containerHeight - displayedHeight) / 2;
    
    // Calculate bounding box in pixels relative to the displayed image
    const boxLeft = offsetX + (x_min * displayedWidth);
    const boxTop = offsetY + (y_min * displayedHeight);
    const boxWidth = (x_max - x_min) * displayedWidth;
    const boxHeight = (y_max - y_min) * displayedHeight;
    
    console.log("Bounding box calculation:", {
      containerWidth,
      containerHeight,
      displayedWidth,
      displayedHeight,
      offsetX,
      offsetY,
      coordinates: { x_min, y_min, x_max, y_max },
      result: { boxLeft, boxTop, boxWidth, boxHeight }
    });
    
    return {
      left: `${boxLeft}px`,
      top: `${boxTop}px`,
      width: `${boxWidth}px`,
      height: `${boxHeight}px`,
    };
  };

  const handleImageLoad = () => {
    console.log("Image loaded, triggering bounding box calculation");
    setImageLoaded(true);
    // Force a re-render after a short delay to ensure dimensions are calculated
    setTimeout(() => {
      setImageLoaded(prev => prev);
    }, 50);
  };

  // For report mode with sub-blocks
  if (enableSubBlocks && subBlocks) {
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          transition: 'all 0.2s',
          border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
          backgroundColor: alpha(theme.palette.warning.main, 0.04),
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`,
          }
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            fontWeight: 500,
            color: theme.palette.warning.dark
          }}
        >
          <LocationOnIcon color="warning" fontSize="small" />
          Lesion Localization
        </Typography>
        
        <Box>
              {subBlocks.map((subBlock, index) => {
                if (typeof subBlock.content !== 'string' && subBlock.content.type === 'image') {
                  return (
                        <Box
                          key={subBlock.id}
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            my: 2,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                            opacity: subBlock.isVisible ? 1 : 0.5,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            transition: "all 0.2s ease",
                            '&:hover': {
                              boxShadow: `0 4px 8px ${alpha(theme.palette.text.primary, 0.1)}`,
                              borderColor: alpha(theme.palette.warning.main, 0.3)
                            }
                          }}
                        >
                          <Stack 
                            direction="row" 
                            justifyContent="flex-end" 
                            alignItems="center" 
                            mb={1.5}
                            spacing={1}
                          >
                            <Tooltip title={subBlock.isVisible ? "Hide" : "Show"}>
                              <IconButton 
                                size="small" 
                                color={!subBlock.isVisible ? "error" : "default"}
                                onClick={() => onToggleVisibility?.(subBlock.id)}
                              >
                                {subBlock.isVisible ? 
                                  <VisibilityIcon fontSize="small" /> : 
                                  <VisibilityOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Stack>

                          <Box sx={{ textAlign: "center" }}>
                            <Chip
                              size="small"
                              label={typeof subBlock.content !== 'string' ? subBlock.content.label : ''}
                              color="default"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                            <Box 
                              sx={{ 
                                position: 'relative',
                                borderRadius: 1, 
                                overflow: 'hidden',
                                width: '100%',
                                height: 280,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <img 
                                ref={imageRef}
                                src={typeof subBlock.content !== 'string' ? subBlock.content.url : ''} 
                                alt={typeof subBlock.content !== 'string' ? subBlock.content.label : ''}
                                onLoad={handleImageLoad}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '280px', 
                                  objectFit: 'contain',
                                  borderRadius: '4px'
                                }}
                              />
                              {/* Bounding box overlay */}
                              {imageLoaded && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    ...getBoundingBoxStyle(),
                                    border: `3px solid ${theme.palette.warning.main}`,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.2),
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                  );
                } else if (typeof subBlock.content === 'string') {
                  return (
                    <SubBlockEditor
                      key={subBlock.id}
                      blockId={blockId}
                      subBlock={subBlock}
                      index={index}
                      onChange={() => {}} // Coordinate info is read-only
                      onToggleVisibility={onToggleVisibility || (() => {})}
                    />
                  );
                }
                return null;
              })}
        </Box>
      </Paper>
    );
  }

  // Regular view (non-report mode)
  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        transition: 'all 0.2s',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.04),
        '&:hover': {
          boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`,
        }
      }}
    >
      <Typography 
        variant="subtitle1" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          fontWeight: 500,
          color: theme.palette.warning.dark
        }}
      >
        <LocationOnIcon color="warning" fontSize="small" />
        Lesion Localization
      </Typography>

      {hasCoordinates ? (
        <Stack spacing={3}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.background.paper, 0.6),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Typography variant="body2" gutterBottom>
              <strong>Coordinates:</strong> ({formatCoord(x_min)}, {formatCoord(y_min)}) to ({formatCoord(x_max)}, {formatCoord(y_max)})
            </Typography>
            
            {typeof confidence === 'number' && (
              <Typography variant="body2" gutterBottom>
                <strong>Confidence:</strong> {Math.round(confidence * 100)}%
              </Typography>
            )}
          </Box>

          {/* Image with detection overlay */}
          {imageUrl && (
            <Box sx={{ position: 'relative', width: '100%', height: 280, overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Lesion detection"
                  onLoad={handleImageLoad}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
                {imageLoaded && (
                  <Box
                    sx={{
                      position: 'absolute',
                      ...getBoundingBoxStyle(),
                      border: `3px solid ${theme.palette.warning.main}`,
                      backgroundColor: alpha(theme.palette.warning.main, 0.2),
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Stack>
      ) : (
        <Alert severity="info">
          No lesion coordinates available. Use the Localization tool to mark the lesion area.
        </Alert>
      )}
    </Paper>
  );
};

const FileInfoBlock: React.FC<{ 
  blockId: string;
  data: BlockData;
  subBlocks?: SubBlock[];
  onToggleVisibility?: (id: string) => void;
  enableSubBlocks?: boolean;
}> = ({ 
  blockId, 
  data, 
  subBlocks,
  onToggleVisibility,
  enableSubBlocks 
}) => {
  const theme = useTheme();

  // Base URL for images
  const baseUrl = process.env.REACT_APP_API_URL_;
  
  // Create the full URL to the image
  const imageUrl = data.filePath ? baseUrl + data.filePath.replace(/^\/+/, "") : "";
  
  if (enableSubBlocks && subBlocks) {
    // Render as draggable sub-blocks in report mode
    return (
      <Paper variant="outlined" sx={{ 
        p: 2, 
        mb: 2, 
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
      }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: "bold", 
            color: theme.palette.text.primary 
          }}
        >
          📂 File Information
        </Typography>
        
        <Box>
              {subBlocks.map((subBlock, index) => (
                <SubBlockEditor
                  key={subBlock.id}
                  blockId={blockId}
                  subBlock={subBlock}
                  index={index}
                  onChange={() => {}} // File info is read-only
                  onToggleVisibility={onToggleVisibility || (() => {})}
                />
              ))}
        </Box>
      </Paper>
    );
  }

  // Original non-report mode rendering
  return (
    <Paper variant="outlined" sx={{ 
      p: 2, 
      mb: 2, 
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
    }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          fontWeight: "bold", 
          color: theme.palette.text.primary 
        }}
      >
        📂 File Information
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>File name:</Box> {data.fileName}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>Path:</Box> {data.filePath}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>Created:</Box> {new Date(data.createdAt).toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>Type:</Box> {data.mimeType}
        </Typography>
      </Box>
      
      {/* Image preview section */}
      {data.mimeType && data.mimeType.startsWith('image/') && data.filePath && (
        <Box sx={{ mt: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: "bold", 
              mb: 1,
              color: theme.palette.text.primary
            }}
          >
            Original Image:
          </Typography>
          <Box 
            sx={{ 
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 1, 
              p: 1, 
              maxHeight: '300px',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            }}
          >
            <img 
              src={imageUrl} 
              alt={data.fileName || "Case image"} 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '280px', 
                objectFit: 'contain'
              }} 
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};
// ...existing code...
const BlockRenderer: React.FC<{
  block: Block;
  onTextChange?: (text: string) => void;
  onEnhance?: (text: string) => void;
  enableSubBlocks?: boolean;
  onSubBlockChange?: (blockId: string, subBlockId: string, content: string) => void;
  onToggleVisibility?: (blockId: string, subBlockId: string) => void;
}> = ({ 
  block, 
  onTextChange, 
  onEnhance, 
  enableSubBlocks,
  onSubBlockChange,
  onToggleVisibility
}) => {
  switch (block.type) {
    case "meta":
      return <MetadataBlock data={block.data} />;
    case "image":
      return <ImageBlock data={block.data} />;
    case "segmentation":
      return (
        <SegmentationBlock 
          blockId={block.id}
          data={block.data}
          subBlocks={block.subBlocks}
          enableSubBlocks={enableSubBlocks}
          onToggleVisibility={(subBlockId) => 
            onToggleVisibility?.(block.id, subBlockId)
          }
        />
      );
    case "detection":
      return (
        <DetectionBlock 
          blockId={block.id}
          data={block.data}
          subBlocks={block.subBlocks}
          enableSubBlocks={enableSubBlocks}
          onToggleVisibility={(subBlockId) => 
            onToggleVisibility?.(block.id, subBlockId)
          }
        />
      );
    case "xai":
      return <XAIBlock data={block.data} />;
    case "llm":
      return (
        <LLMNoteBlock 
          blockId={block.id}
          data={block.data}
          subBlocks={block.subBlocks}
          enableSubBlocks={enableSubBlocks}
          onSubBlockChange={(subBlockId, content) => 
            onSubBlockChange?.(block.id, subBlockId, content)
          }
          onToggleVisibility={(subBlockId) => 
            onToggleVisibility?.(block.id, subBlockId)
          }
        />
      );
    case "text":
      return (
        <TextBlock
          data={block.data}
          onChange={onTextChange || (() => {})}
          onEnhance={onEnhance || (() => {})}
        />
      );
    case "fileInfo":
      return (
        <FileInfoBlock 
          blockId={block.id}
          data={block.data}
          subBlocks={block.subBlocks}
          enableSubBlocks={enableSubBlocks}
          onToggleVisibility={(subBlockId) => 
            onToggleVisibility?.(block.id, subBlockId)
          }
        />
      );
    default:
      return null;
  }
};
// ...existing code...
const SubBlockEditor: React.FC<{
  blockId: string;
  subBlock: SubBlock;
  index: number;
  onChange: (id: string, content: string) => void;
  onToggleVisibility: (id: string) => void;
}> = ({ blockId, subBlock, index, onChange, onToggleVisibility }) => {
  const theme = useTheme();
  
  return (
        <Box
          key={subBlock.id}
          sx={{
            p: { xs: 1, sm: 2 },
            my: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            opacity: subBlock.isVisible ? 1 : 0.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            transition: "all 0.2s ease",
            '&:hover': {
              boxShadow: `0 4px 8px ${alpha(theme.palette.text.primary, 0.1)}`,
              borderColor: alpha(theme.palette.primary.main, 0.3)
            }
          }}
        >
          <Stack 
            direction="row" 
            justifyContent="flex-end" 
            alignItems="center" 
            mb={1.5}
            spacing={1}
          >
            <Tooltip title={subBlock.isVisible ? "Hide" : "Show"}>
              <IconButton 
                size="small" 
                color={!subBlock.isVisible ? "error" : "default"}
                onClick={() => onToggleVisibility(subBlock.id)}
                className="visibility-button"
              >
                {subBlock.isVisible ? 
                  <VisibilityIcon fontSize="small" /> : 
                  <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          
          {typeof subBlock.content === 'string' ? (
            <Box 
              sx={{ 
                whiteSpace: "pre-line",
                p: { xs: 1, sm: 1.5 },
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.background.default, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <ReactMarkdown>{subBlock.content}</ReactMarkdown>
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: "center", 
              p: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 1,
            }}>
              <Chip
                size="small"
                label={subBlock.content.label}
                color="default"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <Box 
                sx={{ 
                  borderRadius: 1, 
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={subBlock.content.url} 
                  alt={subBlock.content.label}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '280px', 
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
  );
};// ...existing code...
            
export default function CaseBlocksRenderer({ caseId, generateReport = false }: CaseBlocksRendererProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlVersion = searchParams.get('version');
  const queryParams = new URLSearchParams(location.search);
  const patientId = queryParams.get("patientId");
  const [activeVersionNumber, setActiveVersionNumber] = useState<number | undefined>(urlVersion ? parseInt(urlVersion) : undefined);
  
  const [fileInfoBlock, setFileInfoBlock] = useState<Block | null>(null);
  const [segmentationBlock, setSegmentationBlock] = useState<Block | null>(null);
  const [llmBlocks, setLlmBlocks] = useState<Block[]>([]); // Hidden LLM blocks for report content
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  
  const authService = React.useMemo(() => new AuthService(), []);

  // Helper function to split text into paragraphs for sub-blocks
  const createSubBlocksFromText = (text: string, blockId: string): SubBlock[] => {
    return text.split('\n\n')
      .filter(paragraph => paragraph.trim().length > 0)
      .map((paragraph, i) => ({
        id: `text-${Date.now()}-${i}`,
        blockId, // Parent block ID
        content: paragraph.trim(),
        isVisible: true
      }));
  };

  // Helper function to create image sub-blocks
  const createImageSubBlocks = (data: BlockData, blockId: string): SubBlock[] => {
    const baseUrl = process.env.REACT_APP_API_URL_;
    const images: SubBlock[] = [];

    // Add segmentation images if available
    const imagePaths = [
      { label: "Segmented Image", path: data.segmentedPlot },
      { label: "Asymmetry Plot 1", path: data.assymetryPlot1 },
      { label: "Asymmetry Plot 2", path: data.assymetryplot2 },
      { label: "Color Histogram", path: data.colorplot },
    ];

    imagePaths.forEach((img, idx) => {
      if (img.path) {
        const fullUrl = baseUrl + img.path.replace(/^\/+/, "");
        images.push({
          id: `img-${Date.now()}-${idx}`,
          blockId,
          content: {
            type: 'image',
            url: fullUrl,
            label: img.label
          },
          isVisible: true
        });
      }
    });

    return images;
  };

  // Helper function to create file info sub-blocks
  const createFileInfoSubBlocks = (data: BlockData, blockId: string): SubBlock[] => {
    const baseUrl = process.env.REACT_APP_API_URL_;
    const subBlocks: SubBlock[] = [];
    
    // Add metadata as text
    subBlocks.push({
      id: `file-info-${Date.now()}`,
      blockId,
      content: `**File Information**\n\nFile name: ${data.fileName}\nPath: ${data.filePath}\nCreated: ${new Date(data.createdAt).toLocaleString()}\nType: ${data.mimeType}`,
      isVisible: true
    });

    // Add original image if it's an image file
    if (data.mimeType && data.mimeType.startsWith('image/') && data.filePath) {
      const imageUrl = data.filePath ? baseUrl + data.filePath.replace(/^\/+/, "") : "";
      subBlocks.push({
        id: `file-img-${Date.now()}`,
        blockId,
        content: {
          type: 'image',
          url: imageUrl,
          label: 'Original Image'
        },
        isVisible: true
      });
    }

    return subBlocks;
  };

  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        const token = await authService.getAccessToken();
        if (!token) throw new Error("No access token available");

        const res = await getCaseBlocks(token, caseId);
        
        // Extract only file info
        if (res.DermCase && res.DermCase.length > 0) {
          const fileBlock: Block = {
            id: "fileInfo",
            type: "fileInfo",
            data: {
              fileName: res.DermCase[0].properties.fileName,
              filePath: res.DermCase[0].properties.filePath,
              createdAt: res.DermCase[0].properties.createdAt,
              mimeType: res.DermCase[0].properties.mimeType,
            },
            positionIndex: 0,
            subBlocks: createFileInfoSubBlocks({
              fileName: res.DermCase[0].properties.fileName,
              filePath: res.DermCase[0].properties.filePath,
              createdAt: res.DermCase[0].properties.createdAt,
              mimeType: res.DermCase[0].properties.mimeType,
            }, "fileInfo")
          };
          setFileInfoBlock(fileBlock);
        }

        // Extract segmentation images if available
        const segBlock = res.SegmentationResult?.[0];
        if (segBlock && (segBlock.data.segmentedPlot || segBlock.data.assymetryPlot1 || 
            segBlock.data.assymetryplot2 || segBlock.data.colorplot)) {
          const segmentation: Block = {
            ...segBlock,
            subBlocks: createImageSubBlocks(segBlock.data, segBlock.id)
          };
          setSegmentationBlock(segmentation);
        }

        // Extract LLM blocks for report content (hidden from UI)
        if (res.LLMInteraction && res.LLMInteraction.length > 0) {
          const processedLLMBlocks = res.LLMInteraction.map(block => ({
            ...block,
            subBlocks: block.data.response ? createSubBlocksFromText(block.data.response, block.id) : []
          }));
          setLlmBlocks(processedLLMBlocks);
        }
      } catch (err) {
        console.error("Failed to fetch case blocks:", err);
      }
    };

    fetchBlocks();
  }, [caseId, authService]);

  const handleToggleVisibility = (blockId: string, subBlockId: string) => {
    const updateBlock = (block: Block | null) => {
      if (!block || block.id !== blockId || !block.subBlocks) return block;
      return {
        ...block,
        subBlocks: block.subBlocks.map(sub =>
          sub.id === subBlockId ? { ...sub, isVisible: !sub.isVisible } : sub
        )
      };
    };
    setFileInfoBlock(prev => updateBlock(prev));
    setSegmentationBlock(prev => updateBlock(prev));
  };

  const generateFinalReport = async () => {
    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");
      
      let textContent = '';
      
      // First, try to get the active version's report content
      try {
        const activeVersion = await getActiveVersion(token, caseId);
        
        if (activeVersion && activeVersion.reportId) {
          // Active version has a report - use its content as base
          console.log(`📄 Using content from active version ${activeVersion.versionNumber} (reportId: ${activeVersion.reportId})`);
          const { getReportById } = await import("../../services/reportAPI");
          const reportData = await getReportById(token, activeVersion.reportId);
          textContent = reportData.markdownContent;
        } else {
          // Active version is v0 (no report) - use LLM blocks/extractions
          console.log(`📄 Active version is v${activeVersion?.versionNumber || 0} (no report) - using LLM blocks`);
          textContent = llmBlocks
            .flatMap(block => 
              block.subBlocks ? block.subBlocks
                .filter(subBlock => subBlock.isVisible && typeof subBlock.content === 'string')
                .map(subBlock => subBlock.content as string) : []
            )
            .join('\n\n');
        }
      } catch (err) {
        console.error('Error fetching active version, falling back to LLM blocks:', err);
        // Fallback to LLM blocks
        textContent = llmBlocks
          .flatMap(block => 
            block.subBlocks ? block.subBlocks
              .filter(subBlock => subBlock.isVisible && typeof subBlock.content === 'string')
              .map(subBlock => subBlock.content as string) : []
          )
          .join('\n\n');
      }
      
      const allBlocks = [fileInfoBlock, segmentationBlock].filter(Boolean) as Block[];
      
      const allSubBlocks = allBlocks
        .flatMap(block => 
          block.subBlocks ? block.subBlocks
            .filter(subBlock => subBlock.isVisible)
            .map(subBlock => ({
              content: subBlock.content,
              isImage: typeof subBlock.content !== 'string'
            })) : []
        );
        
      const imageUrls = allSubBlocks
        .filter(item => item.isImage)
        .map(item => {
          const imageContent = item.content as {type: 'image', url: string, label: string};
          return { url: imageContent.url, label: imageContent.label };
        });
      
      const reportData = {
        caseId,
        patientId,
        title: `Dermatology Case Report: ${caseId}`,
        content: textContent,
        images: imageUrls,
        createdAt: new Date().toISOString()
      };
      
      localStorage.setItem('tempReport', JSON.stringify(reportData));
      navigate(`/cases/${caseId}/report-preview${patientId ? `?patientId=${patientId}` : ''}`);
    } catch (err) {
      console.error('Error generating report:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <FinalizedReportsLinks caseId={caseId} />
      
      <Card 
        elevation={3}
        sx={{
          mb: 4,
          borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(40,44,52,0.95) 0%, rgba(30,33,38,1) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,1) 100%)',
          boxShadow: isDarkMode
            ? '0 12px 40px rgba(0,0,0,0.4)'
            : '0 12px 40px rgba(100,100,200,0.12)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1
                }}
              >
                <ImageIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                Select Images for Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose which images to include in your medical report
              </Typography>
            </Box>
            
            <Button 
              variant="contained" 
              size="large"
              onClick={generateFinalReport}
              disabled={!fileInfoBlock && !segmentationBlock}
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${alpha(theme.palette.success.main, 0.7)} 90%)`,
                boxShadow: `0 4px 14px ${alpha(theme.palette.success.main, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.5)}`,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s'
              }}
            >
              Generate Report
            </Button>
          </Stack>

          <Stack spacing={3}>
            {fileInfoBlock && fileInfoBlock.subBlocks && (
              <FileInfoBlock 
                blockId={fileInfoBlock.id}
                data={fileInfoBlock.data}
                subBlocks={fileInfoBlock.subBlocks}
                enableSubBlocks={true}
                onToggleVisibility={(subBlockId) => 
                  handleToggleVisibility(fileInfoBlock.id, subBlockId)
                }
              />
            )}
            
            {segmentationBlock && segmentationBlock.subBlocks && (
              <SegmentationBlock 
                blockId={segmentationBlock.id}
                data={segmentationBlock.data}
                subBlocks={segmentationBlock.subBlocks}
                enableSubBlocks={true}
                onToggleVisibility={(subBlockId) => 
                  handleToggleVisibility(segmentationBlock.id, subBlockId)
                }
              />
            )}
            
            {!fileInfoBlock && !segmentationBlock && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No images available for this case. Upload images to include them in your report.
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}